import { AxiosError, AxiosResponse } from 'axios';
import qs from 'qs-stringify';

import { RESPONSE_CODES } from '../constants/responseCodes.constants';
import { PaginationFactory, PaginatorFactory } from '../factory';
import { api } from '../http';
import { store } from '../store';
import { DefaultRes, AlertTypes } from '../types';

export class AlertService {
    public static readonly dispatch = store.dispatch.alert;
    public static readonly state = store.getState();

    public static async getAlertDetails(id: string, signal?: AbortSignal) {
        const {
            data: {
                result,
            },
        } = await api.get<
            DefaultRes<AlertTypes.Model>,
            AxiosResponse<DefaultRes<AlertTypes.Model>>
        >(`/alerts/getAlertDetails/${id}`, { signal });

        return result;
    }

    public static async getCountViewed() {
        try {
            const {
                data: {
                    result,
                    isValid,
                },
            } = await api.get<
                DefaultRes<{ viewed: number, unviewed: number, unviewedAlerts: AlertTypes.Model[] }>,
                AxiosResponse<DefaultRes<{ viewed: number, unviewed: number, unviewedAlerts: AlertTypes.Model[] }>>
            >(`/alerts/count`);
    
            if (isValid) {
                return result;
            }
    
            return null;
        } catch (err: any) {
            return null;
        }
    }

    public static async getById(id: string) {
        const {
            data: {
                result,
                isValid,
            },
        } = await api.get<
            DefaultRes<AlertTypes.Model>,
            AxiosResponse<DefaultRes<AlertTypes.Model>>
        >(`/alerts/byId/${id}`);

        if (isValid) {
            return result;
        }

        return null;
    }

    public static async fetchMany(
        dto: PaginatorFactory<{}>,
        callback?: (isValid?: boolean, message?: string) => void,
        ignoreLoading = false,
        updateState = true,
    ): Promise<PaginationFactory<AlertTypes.Model> | void> {
        const {
            dispatch: { toggleLoading, setPagination },
        } = AlertService;

        if (!ignoreLoading) {
            toggleLoading('fetchMany', true);
        }

        try {
            const qsfy = qs({
                perPage: dto.perPage,
                page: dto?.page,
                filter: {
                    ...dto?.filter,
                },
                sort: {
                    ...dto?.sort,
                }
            });

            const {
                data: {
                    result: { items, page, perPage, total },
                    isValid,
                    message,
                },
            } = await api.get<
                DefaultRes<PaginationFactory<AlertTypes.Model>>,
                AxiosResponse<DefaultRes<PaginationFactory<AlertTypes.Model>>>
            >(`/alerts/getMany?${qsfy}&includeAppointments=true`);

            toggleLoading('fetchMany', false);

            if (isValid && updateState) {
                setPagination({ items, page, perPage, total });
            }

            callback && callback(isValid, RESPONSE_CODES[message]);

            return { items, page, perPage, total };
        } catch (error: any) {
            toggleLoading('fetchMany', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<AlertTypes.Model>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
        }
    }

    public static async resolve(
        alertId: string,
        resolvedComment: string,
        alertType: string,
        callback?: (isValid?: boolean, message?: string) => void
    ) {
        const {
            dispatch: { toggleLoading, updatePaginationItem },
        } = AlertService;

        try {
            toggleLoading('resolve', true);

            const {
                data: { isValid, message, result },
            } = await api.post<
                DefaultRes<AlertTypes.Model>,
                AxiosResponse<DefaultRes<AlertTypes.Model>>
            >(`/alerts/resolve`, { alertId, resolvedComment, alertType });

            toggleLoading('resolve', false);

            return callback && callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('resolve', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<AlertTypes.Model>>
                ).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async view(
        alertId: string,
        callback?: (isValid?: boolean, message?: string) => void
    ) {
        // ALERT_VIEW_URI is the stem for making a request to the API to set an alert to 'viewed'
        const ALERT_VIEW_URI = `/alerts/view`;

        const {
            dispatch: { toggleLoading, updatePaginationItem },
        } = AlertService;

        try {
            toggleLoading('resolve', true);

            const {
                data: { isValid, message, result },
            } = await api.post<
                DefaultRes<AlertTypes.Model>,
                AxiosResponse<DefaultRes<AlertTypes.Model>>
            >(ALERT_VIEW_URI, { alertId });

            toggleLoading('resolve', false);

            if (isValid) {
                updatePaginationItem(result);
            }

            return callback && callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('resolve', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<AlertTypes.Model>>
                ).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }
}
